import React, { useState } from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { useNavigationState } from "@react-navigation/native";
import { AppScreens } from "../../screens/Utility/AppScreens";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import MoreTab from "../../Components/MoreTab";
import CustomChatFooterDrawer from "../CustomChatFooterDrawer";
import CustomNotificationFooterDrawer from "../CustomNotificationFooterDrawer";
import CustomHomeFooterDrawer from "../CustomHomeFooterDrawer";
import {
  HomeTabStack,
  MyEventsTabStack,
  ChatTabStack,
  NotificationTabStack,
  ProfileTabStack,
  PurchaseTabStack,
  SettingTabStack,
  HelpTabStack,
} from "./StackNavigators";

const Tab = createBottomTabNavigator();

export default function MobileNavigation() {
  const [isMoreModalVisible, setMoreModalVisible] = useState(false);

  // Get current route name and current tab from navigation state
  const { currentRouteName, currentTabName } = useNavigationState((state) => {
    if (!state) return { currentRouteName: null, currentTabName: null };

    // Recursively find the deepest route name and the tab level
    const getNavigationInfo = (navigationState, level = 0) => {
      // Safety checks to prevent crashes
      if (
        !navigationState ||
        !navigationState.routes ||
        navigationState.routes.length === 0
      ) {
        return { screenName: null, tabName: null };
      }

      const route = navigationState.routes[navigationState.index];

      // Safety check: ensure route exists
      if (!route) {
        console.warn(
          "MobileNavigation: Route is undefined at index",
          navigationState.index
        );
        return { screenName: null, tabName: null };
      }

      // If this route has nested state, go deeper
      if (route.state) {
        const deeper = getNavigationInfo(route.state, level + 1);

        // Level 1 is typically the tab navigator level (after drawer)
        if (level === 1) {
          return {
            screenName: deeper.screenName || deeper.tabName,
            tabName: route.name,
          };
        }

        return deeper.screenName
          ? deeper
          : { screenName: route.name, tabName: deeper.tabName || route.name };
      }

      // Return the route name at the deepest level
      return { screenName: route.name, tabName: route.name };
    };

    const info = getNavigationInfo(state);

    return {
      currentRouteName: info.screenName,
      currentTabName: info.tabName,
    };
  });

  // Map screens to their corresponding tab sections
  // This determines which tab icon should be highlighted based on current screen
  const getActiveTabForScreen = (screenName, tabName) => {
    if (!screenName) return null;

    // Check for More Tab Stacks first using tabName
    if (
      ["ProfileTabStack", "SettingTabStack", "HelpTabStack"].includes(tabName)
    ) {
      return "More";
    }

    // More Tab Screens (Help, Settings, Profile) - CHECK FIRST!
    // These screens should ALWAYS highlight the More tab, regardless of which tab they're accessed from
    const moreScreens = [
      AppScreens.HelpScreen,
      "Help", // Alternative name
      AppScreens.SettingWebScreen,
      "SettingWebScreen", // Alternative name
      AppScreens.Profile,
      "Profile", // Alternative name
      AppScreens.ChangeAvatar,
      AppScreens.ContactUsCenter,
      AppScreens.DisputeCenter,
      AppScreens.RefundRequestScreen,
    ];
    if (moreScreens.includes(screenName)) return "More";

    // Direct tab stack names (when on the initial screen of a tab)
    if (screenName === "HomeTabStack" || tabName === "HomeTabStack")
      return "HomeTabStack";
    if (screenName === "MyEventsTabStack" || tabName === "MyEventsTabStack")
      return "MyEventsTabStack";
    if (screenName === "ChatTabStack" || tabName === "ChatTabStack")
      return "ChatTabStack";
    if (
      screenName === "NotificationTabStack" ||
      tabName === "NotificationTabStack"
    )
      return "NotificationTabStack";

    // Home Tab Screens
    const homeScreens = [
      "HomeScreen",
      AppScreens.SearchEventWebScreen,
      AppScreens.SearchGameWebScreen,
    ];
    if (homeScreens.includes(screenName)) return "HomeTabStack";

    // Events Tab Screens
    const eventScreens = [
      "MyEventsScreen",
      AppScreens.CreatePublicEventScreen,
      AppScreens.CreatePrivateEventScreen,
      AppScreens.UpdatePublicEventScreen,
      AppScreens.UpdatePrivateEventScreen,
      AppScreens.EventSummary,
      AppScreens.PrivateEventSummary,
      AppScreens.CreateTicketEventScreen,
      AppScreens.OnGoingEvents,
      AppScreens.OnGoingEventsStreamingSetup,
      AppScreens.GoLive,
      AppScreens.LiveStreaming,
      AppScreens.PastEvent,
      AppScreens.StreamSetup,
    ];
    if (eventScreens.includes(screenName)) return "MyEventsTabStack";

    // Chat Tab Screens
    const chatScreens = [AppScreens.Chat, AppScreens.AddFriends];
    if (chatScreens.includes(screenName)) return "ChatTabStack";

    // Notification Tab Screens
    const notificationScreens = [
      AppScreens.NotificationScreen,
      "Notifications",
    ];
    if (notificationScreens.includes(screenName)) return "NotificationTabStack";

    // Shared screens - these can appear in multiple tabs, use tabName as fallback
    const sharedScreens = [
      AppScreens.PurchaseScreen,
      AppScreens.TicketDetailWebScreen,
      AppScreens.ViewTrasactionHistory,
      AppScreens.TicketHistory,
      AppScreens.UserProfile,
      AppScreens.GameShowScreen,
      AppScreens.SearchMobileScreen,
    ];
    if (sharedScreens.includes(screenName)) {
      // For shared screens, use the tab they're accessed from
      if (tabName === "HomeTabStack") return "HomeTabStack";
      if (tabName === "MyEventsTabStack") return "MyEventsTabStack";
      if (tabName === "ChatTabStack") return "ChatTabStack";
      if (tabName === "NotificationTabStack") return "NotificationTabStack";
      return null;
    }

    // Default: return null (no specific tab highlighted)
    return null;
  };

  // Determine which tab should be highlighted (pass both screen name and tab name)
  let activeTab = getActiveTabForScreen(currentRouteName, currentTabName);

  // Debug: Log current route and active tab
  console.log(
    "MobileNav - Current Route:",
    currentRouteName,
    "| Current Tab:",
    currentTabName,
    "| Active Tab:",
    activeTab
  );

  const toggleMoreModal = () => {
    setMoreModalVisible(!isMoreModalVisible);
  };

  return (
    <>
      <View style={{ flex: 1 }}>
        <LinearGradient
          colors={[AppColors.black, AppColors.sidebarBackground_111827]}
          start={[0, 0]}
          end={[1, 1]}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          }}
        />
        <Tab.Navigator
          screenOptions={{
            headerShown: false,
            gestureEnabled: false,
            tabBarStyle: {
              position: "relative",
              backgroundColor: AppColors.sidebarBackground_111827,
              height: 60,
              paddingTop: 0,
              borderWidth: 0,
              borderColor: AppColors.sidebarBackground_111827,
            },
          }}
          initialRouteName="HomeTabStack"
        >
          <Tab.Screen
            options={{
              tabBarButton: () => null,
              tabBarShowLabel: false,
            }}
            name={AppScreens.ProfileMain}
            component={ProfileTabStack}
          />
          <Tab.Screen
            options={{
              tabBarButton: () => null,
              tabBarShowLabel: false,
            }}
            name={AppScreens.PurchaseTabStack}
            component={PurchaseTabStack}
          />
          <Tab.Screen
            options={{
              tabBarButton: () => null,
              tabBarShowLabel: false,
            }}
            name="SettingTabStack"
            component={SettingTabStack}
          />
          <Tab.Screen
            options={{
              tabBarButton: () => null,
              tabBarShowLabel: false,
            }}
            name="HelpTabStack"
            component={HelpTabStack}
          />
          <Tab.Screen
            name="HomeTabStack"
            component={HomeTabStack}
            listeners={({ navigation }) => ({
              tabPress: (e) => {
                if (activeTab === "HomeTabStack") {
                  e.preventDefault();
                  navigation.reset({
                    index: 0,
                    routes: [{ name: "HomeTabStack" }],
                  });
                }
              },
            })}
            options={{
              tabBarShowLabel: false,
              tabBarIcon: ({ focused }) => (
                <CustomHomeFooterDrawer
                  toggleMoreModal={toggleMoreModal}
                  focused={focused}
                />
              ),
            }}
          />
          <Tab.Screen
            name={"MyEventsTabStack"}
            component={MyEventsTabStack}
            listeners={({ navigation }) => ({
              tabPress: (e) => {
                if (activeTab === "MyEventsTabStack") {
                  e.preventDefault();
                  navigation.reset({
                    index: 0,
                    routes: [{ name: "MyEventsTabStack" }],
                  });
                }
              },
            })}
            options={{
              backgroundColor: AppColors.sidebarBackground_111827,
              tabBarShowLabel: false,
              tabBarIcon: ({ focused }) => {
                const iconColor = focused
                  ? AppColors.buttonRed_DC2626
                  : "#FFFFFF";
                const textColor = focused
                  ? AppColors.buttonRed_DC2626
                  : "#FFFFFF";
                return (
                  <View
                    style={{
                      paddingVertical: 5,
                      gap: 5,
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={AppImages.icTV}
                      style={{
                        width: 27,
                        height: 27,
                        resizeMode: "contain",
                        tintColor: iconColor,
                      }}
                    />
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_12,
                        { color: textColor },
                      ]}
                    >
                      Events
                    </Text>
                  </View>
                );
              },
            }}
          />
          <Tab.Screen
            name={"ChatTabStack"}
            component={ChatTabStack}
            listeners={({ navigation }) => ({
              tabPress: (e) => {
                if (activeTab === "ChatTabStack") {
                  e.preventDefault();
                  navigation.reset({
                    index: 0,
                    routes: [{ name: "ChatTabStack" }],
                  });
                }
              },
            })}
            options={{
              tabBarShowLabel: false,
              tabBarIcon: ({ focused }) => (
                <CustomChatFooterDrawer focused={focused} />
              ),
            }}
          />
          <Tab.Screen
            name={"NotificationTabStack"}
            component={NotificationTabStack}
            listeners={({ navigation }) => ({
              tabPress: (e) => {
                if (activeTab === "NotificationTabStack") {
                  e.preventDefault();
                  navigation.reset({
                    index: 0,
                    routes: [{ name: "NotificationTabStack" }],
                  });
                }
              },
            })}
            options={{
              backgroundColor: AppColors.sidebarBackground_111827,
              tabBarShowLabel: false,
              tabBarIcon: ({ focused }) => (
                <CustomNotificationFooterDrawer
                  toggleMoreModal={toggleMoreModal}
                  focused={focused}
                />
              ),
            }}
          />
          <Tab.Screen
            name="More"
            component={View} // Dummy component since we prevent default
            listeners={() => ({
              tabPress: (e) => {
                e.preventDefault();
                toggleMoreModal();
              },
            })}
            options={{
              tabBarIcon: () => {
                const isActive = activeTab === "More";
                const iconColor = isActive
                  ? AppColors.buttonRed_DC2626
                  : "#FFFFFF";
                const textColor = isActive
                  ? AppColors.buttonRed_DC2626
                  : "#FFFFFF";

                return (
                  <View
                    style={{
                      alignItems: "center",
                      paddingVertical: 5,
                      gap: 5,
                    }}
                  >
                    <Image
                      source={AppImages.WhiteThreeLines}
                      style={{ width: 28, height: 28, tintColor: iconColor }}
                    />
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_12,
                        { color: textColor },
                      ]}
                    >
                      More
                    </Text>
                  </View>
                );
              },
            }}
          />
        </Tab.Navigator>
      </View>
      <MoreTab
        isVisible={isMoreModalVisible}
        onClose={toggleMoreModal}
        toggleMoreModal={toggleMoreModal}
      />
    </>
  );
}
